import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { createTheme } from '@mui/material';
import getThemeOptions from '../theme';
import settingsAPI from '../api/settings';

export const ThemeContext = createContext();

const defaultThemeConfig = {
  primaryColor: '#1565c0',
  secondaryColor: '#ff6b35',
  sidebarColor: '#0d1b2a',
  sidebarTextColor: '#c8d6e5',
  headerColor: '#ffffff',
  headerTextColor: '#1a2027',
  mode: 'light',
  fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
  borderRadius: 10,
};

export const ThemeProvider = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState(() => {
    const stored = localStorage.getItem('koorerp_theme');
    return stored ? JSON.parse(stored) : defaultThemeConfig;
  });
  const [appName, setAppName] = useState(localStorage.getItem('koorai_one_appName') || 'KoorAI ONE');
  const [appLogo, setAppLogo] = useState(localStorage.getItem('koorerp_appLogo') || null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchTheme();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const muiTheme = useMemo(() => {
    const mode = themeConfig.mode || 'light';
    const baseOptions = getThemeOptions(mode, {
      primaryColor: themeConfig.primaryColor,
      secondaryColor: themeConfig.secondaryColor,
    });
    return createTheme(baseOptions);
  }, [themeConfig]);

  const toggleDarkMode = () => {
    setThemeConfig(prev => {
      const updated = { ...prev, mode: prev.mode === 'dark' ? 'light' : 'dark' };
      localStorage.setItem('koorerp_theme', JSON.stringify(updated));
      return updated;
    });
  };

  const fetchTheme = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await settingsAPI.getTheme();
      if (response.data.success) {
        const { theme, appName: name, appLogo: logo } = response.data;
        if (theme) {
          const merged = { ...defaultThemeConfig, ...theme };
          setThemeConfig(merged);
          localStorage.setItem('koorerp_theme', JSON.stringify(merged));
        }
        if (name) {
          setAppName(name);
          localStorage.setItem('koorai_one_appName', name);
        }
        if (logo !== undefined) {
          setAppLogo(logo);
          if (logo) localStorage.setItem('koorerp_appLogo', logo);
          else localStorage.removeItem('koorerp_appLogo');
        }
      }
    } catch (error) {
      console.error('Failed to fetch theme:', error);
    }
  };

  const updateThemeConfig = (newConfig) => {
    setThemeConfig(prev => {
      const updated = { ...prev, ...newConfig };
      localStorage.setItem('koorerp_theme', JSON.stringify(updated));
      return updated;
    });
  };

  const updateAppBranding = (name, logo) => {
    if (name !== undefined) {
      setAppName(name);
      localStorage.setItem('koorai_one_appName', name);
    }
    if (logo !== undefined) {
      setAppLogo(logo);
      if (logo) localStorage.setItem('koorerp_appLogo', logo);
      else localStorage.removeItem('koorerp_appLogo');
    }
  };

  const value = {
    themeConfig,
    muiTheme,
    appName,
    appLogo,
    isDarkMode: themeConfig.mode === 'dark',
    fetchTheme,
    toggleDarkMode,
    updateThemeConfig,
    updateAppBranding,
    defaultThemeConfig,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};
